import {
    Button,
    Form,
    Input,
    Popconfirm,
    Table,
    Tooltip,
    Typography,
} from "antd";
import type { ColumnsType } from "antd/lib/table";
import { observer } from "mobx-react-lite";
import React from "react";
import ActionDefinitionStore, {
    ActionRoleSummary,
} from "../stores/ActionDefinitionStore";
import AppModal from "../../common/components/AppModal";

const { Text } = Typography;

type Props = {
    store: ActionDefinitionStore;
};

const ManageActionRolesDialog: React.FC<Props> = ({ store }) => {
    const [roleEditorVisible, setRoleEditorVisible] = React.useState(false);
    const [editingRoleName, setEditingRoleName] = React.useState<string | null>(
        null,
    );
    const [form] = Form.useForm<{
        roleName: string;
        description?: string;
    }>();

    const isEditing = editingRoleName !== null;

    React.useEffect(() => {
        if (!store.actionRolesDialogVisible) {
            closeRoleEditor();
        }
    }, [store.actionRolesDialogVisible]);

    const closeRoleEditor = () => {
        setRoleEditorVisible(false);
        setEditingRoleName(null);
        form.resetFields();
    };

    const openAddRoleDialog = () => {
        setEditingRoleName(null);
        form.setFieldsValue({ roleName: "", description: "" });
        setRoleEditorVisible(true);
    };

    const openEditRoleDialog = (roleName: string) => {
        const role = store.actionRoleSummaries.find(
            (item) => item.name === roleName,
        );
        setEditingRoleName(roleName);
        form.setFieldsValue({
            roleName,
            description: role?.description ?? "",
        });
        setRoleEditorVisible(true);
    };

    const handleRoleEditorSubmit = async () => {
        const values = await form.validateFields();
        const currentRole = store.actionRoleSummaries.find(
            (role) => role.name === editingRoleName,
        );
        const isSuccess = isEditing
            ? await store.renameActionRole(
                  currentRole!.id,
                  editingRoleName!,
                  values.roleName,
                  values.description,
              )
            : await store.addActionRole({
                  name: values.roleName,
                  description: values.description,
              });

        if (isSuccess) {
            closeRoleEditor();
        }
    };

    const validateRoleName = (_: unknown, value?: string) => {
        const normalizedValue = value?.trim().toLowerCase() ?? "";

        if (!normalizedValue) {
            return Promise.resolve();
        }

        const hasDuplicate = store.actionRoleSummaries.some((role) => {
            if (
                isEditing &&
                editingRoleName &&
                role.name.toLowerCase() === editingRoleName.toLowerCase()
            ) {
                return false;
            }

            return role.name.trim().toLowerCase() === normalizedValue;
        });

        if (hasDuplicate) {
            return Promise.reject(
                new Error("Role with the same name already exists"),
            );
        }

        return Promise.resolve();
    };

    const columns: ColumnsType<ActionRoleSummary> = [
        {
            key: "name",
            dataIndex: "name",
            title: "Role name",
        },
        {
            key: "usedInActions",
            dataIndex: "usedInActions",
            title: "Used in actions",
            width: 180,
            render: (usedInActions: number) =>
                `${usedInActions} action${usedInActions === 1 ? "" : "s"}`,
        },
        {
            key: "actions",
            title: "Actions",
            width: 120,
            render: (_, record) => {
                const isDeleteDisabled =
                    record.usedInActions > 0 || store.actionRolesLoading;
                const deleteDisabledReason =
                    record.usedInActions > 0
                        ? "This role cannot be deleted because it is used in one or more actions."
                        : undefined;

                const deleteButton = (
                    <Button
                        type="link"
                        size="small"
                        danger={record.usedInActions === 0}
                        disabled={isDeleteDisabled}
                    >
                        <i className="alpha-icon md table-action-delete" />
                    </Button>
                );

                return (
                    <div className="row-actions-wrapper visible">
                        <Button
                            type="link"
                            size="small"
                            title="Edit action role"
                            onClick={() => openEditRoleDialog(record.name)}
                            disabled={store.actionRolesLoading}
                        >
                            <i className="alpha-icon md table-action-edit" />
                        </Button>
                        {deleteDisabledReason ? (
                            <Tooltip title={deleteDisabledReason}>
                                <span>{deleteButton}</span>
                            </Tooltip>
                        ) : (
                            <Popconfirm
                                title="Are you sure you want to delete this action role?"
                                onConfirm={() =>
                                    store.deleteActionRole(
                                        record.id,
                                        record.name,
                                    )
                                }
                                placement="topRight"
                                disabled={isDeleteDisabled}
                            >
                                {deleteButton}
                            </Popconfirm>
                        )}
                    </div>
                );
            },
        },
    ];

    return (
        <>
            <AppModal
                open={store.actionRolesDialogVisible}
                onCancel={() => store.setActionRolesDialogVisible(false)}
                title={
                    <span className="dialog-title">Manage action roles</span>
                }
                destroyOnClose
                centered
                width={760}
                className="alpha-modal"
                closable={false}
                footer={
                    <div
                        style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                        }}
                    >
                        <Button
                            size="large"
                            onClick={openAddRoleDialog}
                            type="primary"
                        >
                            Add role
                        </Button>
                        <Button
                            size="large"
                            className="light"
                            onClick={() =>
                                store.setActionRolesDialogVisible(false)
                            }
                        >
                            Close
                        </Button>
                    </div>
                }
            >
                {!store.actionRolesLoading &&
                store.availableActionRoles.length === 0 ? (
                    <div
                        style={{
                            padding: "24px 0 32px",
                        }}
                    >
                        <Text type="secondary">
                            There are no action roles for this project yet.
                        </Text>
                    </div>
                ) : (
                    <Table
                        className="alpha-table"
                        columns={columns}
                        dataSource={store.actionRoleSummaries}
                        rowKey={(role) => role.name}
                        loading={store.actionRolesLoading}
                        pagination={false}
                        style={{ marginBottom: 16 }}
                    />
                )}
            </AppModal>
            <AppModal
                open={roleEditorVisible}
                onCancel={closeRoleEditor}
                destroyOnClose
                centered
                width={480}
                className="alpha-modal"
                closable={false}
                title={
                    <span className="dialog-title">
                        {isEditing ? "Edit action role" : "Add action role"}
                    </span>
                }
                cancelButtonTitle="Cancel"
                onCancelClick={closeRoleEditor}
                cancelButtonProps={{
                    className: undefined,
                }}
                submitButtonTitle={isEditing ? "Save" : "Add role"}
                onSubmitClick={handleRoleEditorSubmit}
                submitButtonProps={{
                    loading: store.actionRolesLoading,
                }}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="roleName"
                        label="Role name"
                        rules={[
                            {
                                required: true,
                                message: "Role name is required",
                            },
                            {
                                validator: validateRoleName,
                            },
                        ]}
                    >
                        <Input
                            maxLength={150}
                            autoFocus
                            placeholder="Enter role name"
                        />
                    </Form.Item>
                    <Form.Item name="description" label="Description">
                        <Input.TextArea
                            maxLength={1000}
                            rows={4}
                            placeholder="Enter description"
                        />
                    </Form.Item>
                </Form>
            </AppModal>
        </>
    );
};

export default observer(ManageActionRolesDialog);
